import { Box, Button, CircularProgress, Popover, Typography } from '@mui/material';
import { DownloadSimple } from '@phosphor-icons/react';
import { Editor } from '@tinymce/tinymce-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { axiosInstance } from '../../../service/ApiService';
import { useAuthStore } from '../../../store/storeAuth';
import { useNotificationStore } from '../../../store/storeNotifications';
import { TextComponent } from '../../UI/PreviewModal/PreviewModal';

const Output = ({ data, output }: any) => {
  const value: any = output?.text || '';
  const editorRef = useRef(null);
  const [editorContent, setEditorContent] = useState(output?.text || '');
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);

  const setNotification = useNotificationStore(state => state.setNotification);
  const onCopy = (text: string) => {
    navigator.clipboard.writeText(text);

    setNotification({
      type: 'success',
      message: 'Text copied to clipboard',
    });
  };
  const user = useAuthStore(state => state.user);

  useEffect(() => {
    if (data?.outputType === 'editor') {
      setEditorContent(output?.text);
    }
  }, [data]);

  const handleEditorChange = (content: string) => {
    setEditorContent(content);
  };

  const handleDownloadClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleDownloadClose = () => {
    setAnchorEl(null);
  };

  const handleDownloadFormat = useCallback(
    async (fileType: 'docx' | 'pdf') => {
      setIsDownloading(true);
      setAnchorEl(null);
      
      try {
        const {
          data: { data: fileLink },
        } = await axiosInstance.post('/upload/text', {
          text: output?.text || '',
          fileType,
        });

        const { data: res } = await axiosInstance.get('/upload/presigned-url', {
          params: {
            url: fileLink.src,
          },
        });

        const urlContentBlob = new Blob(
          [new Uint8Array(res.data.file.data).buffer],
          {
            type: fileType,
          },
        );

        const url = URL.createObjectURL(urlContentBlob);
        const link = document.createElement('a');
        link.href = url;
        link.download = fileLink.name;
        document.body.appendChild(link);
        link.click();
      } catch (error: any) {
        setNotification({ type: 'error', message: error.message });
      }
      setIsDownloading(false);
    },
    [data.fileLink, output, setNotification],
  );

  const open = Boolean(anchorEl);
  const id = open ? 'download-popover' : undefined;

  const hasLabel = data.label !== '' && data.label;

  return (
    <Box
      sx={{
        padding: '16px',
        height: 'fit-content',
        bgcolor: data?.fullData?.bgColor || '#fff',
        borderRadius: data?.fullData?.borderRadius || '0px',
        ...data?.style,
        overflowY: 'auto',
      }}
    >
      <Box
        display={'flex'}
        justifyContent={'space-between'}
        alignItems={'center'}
        mb={'8px'}
      >
        <Typography
          sx={{
            fontSize: '16px',
            fontWeight: '600',
            color: '#475467',
            ...data?.fullData?.titleStyle,
          }}
          variant="h1"
        >
          {hasLabel ? data.label : 'Title output'}
        </Typography>

        {output?.text.length > 0 && (
          <>
            <Button
              onClick={handleDownloadClick}
              size="small"
              disabled={isDownloading}
              sx={{
                padding: '0px',
                borderRadius: '8px',
                border: '1px solid #E4E7EC',
                backgroundColor: 'transparent',
                color: '#101828',
                height: '30px',
                minWidth: '40px',
                cursor: isDownloading ? 'not-allowed' : 'pointer',
              }}
            >
              {isDownloading ? (
                <CircularProgress size="10px" sx={{ color: '#3650de' }} />
              ):  <DownloadSimple size={14} />}
            </Button>

            <Popover
              id={id}
              open={open}
              anchorEl={anchorEl}
              onClose={handleDownloadClose}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
            >
              <Box
                sx={{ p: 1, display: 'flex', flexDirection: 'column', gap: 1 }}
              >
                <Button
                  variant="text"
                  onClick={() => handleDownloadFormat('docx')}
                  fullWidth
                  size="small"
                  sx={{ justifyContent: 'flex-start', fontSize: '12px' }}
                >
                  Download as DOCX
                </Button>
                <Button
                  variant="text"
                  onClick={() => handleDownloadFormat('pdf')}
                  fullWidth
                  size="small"
                  sx={{ justifyContent: 'flex-start', fontSize: '12px' }}
                >
                  Download as PDF
                </Button>
              </Box>
            </Popover>
          </>
        )}
      </Box>

      {data?.subtitle && (
        <Typography
          sx={{
            fontSize: '14px',
            fontWeight: '400',
            color: '#98A2B3',
            ...data?.fullData?.subtitleStyle,
          }}
          mb={'8px'}
          variant="h1"
        >
          {data?.subtitle ? data.subtitle : 'Title Component'}
        </Typography>
      )}

      {data?.outputType === 'standard' && value.length > 0 && (
        <TextComponent
          item={{
            text: output?.text,
            label: 'Output',
            edit: false,
            name: 'output',
          }}
          onCopy={onCopy}
          user={user}
          processedSegments={output?.text}
        />
      )}

      {data?.outputType === 'editor' && value.length > 0 && (
        <Editor
          key={user?.theme}
          id={data.myId}
          apiKey={process.env.REACT_APP_TINYMCE_API_KEY}
          cloudChannel="5"
          ref={editorRef}
          scriptLoading={{
            async: true,
          }}
          value={editorContent}
          onEditorChange={handleEditorChange}
          init={{
            branding: false,
            skin: user?.theme === 'dark' ? 'oxide-dark' : '',
            content_css: user?.theme === 'dark' ? 'dark' : '',
            resize: false,
            width: '100%',
            height: '600px',
            plugins: [
              'advlist autolink lists link image',
              'wordcount',
              'paste',
              'code',
              'table',
              'charmap print preview anchor help',
              'searchreplace visualblocks code',
              'insertdatetime media table paste wordcount',
            ],
            toolbar:
              'undo redo | formatselect | bold italic | \
          alignleft aligncenter alignright | \
          bullist numlist outdent indent | wordcount | code | tableprops tabledelete | tableinsertrowbefore tableinsertrowafter tabledeleterow | tableinsertcolbefore tableinsertcolafter tabledeletecol',
          }}
        />
      )}
    </Box>
  );
};

export default Output;
